/**
 * Blog pricing copy derived from AI_EMPLOYEE_PLANS.
 * Keep blog posts, adapters, and scripts in sync with marketing pricing.
 */

import {
  AI_EMPLOYEE_PLANS,
  formatAiEmployeePlanPriceLabel,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import { SITEGLUE_LIVE_DEMO_GALLERY_ROUTE, SITEGLUE_SIGNUP_CTA_LABEL } from "~/lib/siteglue/cta-config";

export const SITEGLUE_BLOG_PRICING_URL = "/pricing";
/** Get Started Free — signup with fresh hire session. */
export const SITEGLUE_BLOG_SIGNUP_URL =
  "https://app.easygpt.builders/agents/hire/custom?new=1&brand=siteglue";
export const SITEGLUE_BLOG_AGENTS_URL = SITEGLUE_LIVE_DEMO_GALLERY_ROUTE;

const PLAN_CREDITS: Record<Exclude<AiEmployeePlanId, "enterprise">, string> = {
  intern: "250 conversation credits/mo",
  associate: "2,000 conversation credits/mo",
  professional: "10,000 conversation credits/mo",
};

const PLAN_CHANNEL_BLURBS: Record<AiEmployeePlanId, string> = {
  intern: "website chat starter",
  associate: "phone, email, and voice",
  professional: "full-channel coverage (chat, phone, SMS, email, video, WhatsApp, Messenger)",
  enterprise: "org-scale volume, routing, and custom SiteGlue Agent configuration",
};

function planBullet(planId: AiEmployeePlanId): string {
  const plan = AI_EMPLOYEE_PLANS.find((p) => p.id === planId)!;
  const price = formatAiEmployeePlanPriceLabel(planId);
  const credits =
    planId === "enterprise" ? "custom conversation volume" : PLAN_CREDITS[planId];
  return `- **${plan.name}** — ${price} · ${PLAN_CHANNEL_BLURBS[planId]} · ${credits}`;
}

/** Full markdown section replacing legacy “Unit-based pricing” blocks. */
export function siteGlueAiEmployeePricingSectionMarkdown(): string {
  return `## SiteGlue Agent pricing plans

SiteGlue pricing is built around **one SiteGlue Agent**—not unit add-ons or opaque flat bundles. Begin with Intern, then scale channels and Human-in-the-Loop™ collaboration as you grow. Configure your Agent on the [pricing page](${SITEGLUE_BLOG_PRICING_URL}):

${AI_EMPLOYEE_PLANS.map((p) => planBullet(p.id)).join("\n")}

Always a Human-in-the-Loop™ — Team Handoff™, Team Takeover™, and Human Approval keep people in control.`;
}

/** One-line pricing summary for comparison tables / short callouts. */
export function siteGlueAiEmployeePricingShortLine(): string {
  return `SiteGlue Agent plans — Intern FREE, Associate $99/mo, Professional $299/mo, Enterprise custom. See [pricing](${SITEGLUE_BLOG_PRICING_URL}).`;
}

/** Primary CTA used at the end of blog posts. */
export function siteGlueAiEmployeePricingCtaMarkdown(): string {
  return `Set up your SiteGlue Agent on Intern — [${SITEGLUE_SIGNUP_CTA_LABEL}](${SITEGLUE_BLOG_SIGNUP_URL}) (no credit card required), or [see pricing](${SITEGLUE_BLOG_PRICING_URL}).`;
}

/** Compact CTA row used in many post footers. */
export function siteGlueAiEmployeePricingFooterLinksMarkdown(
  extras: { label: string; href: string }[] = [],
): string {
  const links = [
    { label: SITEGLUE_SIGNUP_CTA_LABEL, href: SITEGLUE_BLOG_SIGNUP_URL },
    { label: "See pricing", href: SITEGLUE_BLOG_PRICING_URL },
    { label: "Browse Agents", href: SITEGLUE_BLOG_AGENTS_URL },
    ...extras,
  ];
  return links.map((l) => `[${l.label}](${l.href})`).join(" · ");
}

/** Example stack for practices that need phone + scheduling (Associate/Professional). */
export function siteGlueAiEmployeePricingExamplePhoneScheduling(): string {
  return `A practice that needs phone coverage and scheduling typically starts on **Associate ($99/mo)** or **Professional ($299/mo)**, depending on channel and volume needs. Website chat alone is included on **Intern (FREE)**.`;
}

/** Phrases that must not appear in SiteGlue blog pricing copy. */
export const BLOG_PRICING_FORBIDDEN_PHRASES = [
  "unit-based pricing",
  "$0.50/min",
  "$0.10/SMS",
  "$0.25/min",
  "Try It Free",
  "pay-per-use",
  "Agent Templates",
  "Moonlight templates",
  "pricing calculator",
] as const;
